import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { AffiliateApplicationFormConfig } from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplicationFormConfig } from '../infra/mappers';

interface EnableFormConfigParams {
  projectId: string;
  enabled: boolean;
}

interface UseEnableFormConfigResult {
  enableFormConfig: (params: EnableFormConfigParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useEnableFormConfig = (): UseEnableFormConfigResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplicationFormConfig, Error, EnableFormConfigParams> = useMutation({
    mutationFn: async ({ projectId, enabled }: EnableFormConfigParams) => {
      const response = await affiliateApplicationService.enableFormConfig(projectId, enabled);
      return transformAffiliateApplicationFormConfig(response);
    },
    onSuccess: (data, variables) => {
      toast.success(variables.enabled ? 'Form enabled' : 'Form disabled');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application-form-config', variables.projectId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update form status');
    },
  });

  return {
    enableFormConfig: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
